// Управление воспроизведением шагов (FR-BOARD-06/07): play/pause, шаг вперёд/назад, повтор, скорость, паузы.
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { StepTimeline } from "@brovka/content-types";
import type { BoardHandle } from "./Board.tsx";
import { endPose, poseAt } from "./timeline.ts";

export type Playback = {
  step: number;
  playing: boolean;
  speed: number;
  /** Шаг доигран до конца. */
  stepDone: boolean;
  /** Последний шаг доигран. */
  finished: boolean;
  play(): void;
  pause(): void;
  toggle(): void;
  next(): void;
  prev(): void;
  replay(): void;
  restart(): void;
  setSpeed(v: number): void;
  goTo(step: number): void;
};

function reducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * Проиграть шаги на доске. `pauseAfter(i)` — остановиться после шага i до нажатия «далее».
 * При `prefers-reduced-motion` переходы короткие (0,3 с), но шаги и управление сохраняются.
 */
export function usePlayback(steps: StepTimeline[], board: RefObject<BoardHandle | null>, opts: { autoplay?: boolean; pauseAfter?: (i: number) => boolean; onStep?: (i: number) => void } = {}): Playback {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(Boolean(opts.autoplay));
  const [speed, setSpeed] = useState(1);
  const [stepDone, setStepDone] = useState(false);
  const t = useRef(0);
  const raf = useRef<number | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const show = useCallback(
    (i: number, at: number) => {
      const s = steps[i];
      if (s) board.current?.setPose(at >= s.duration ? endPose(s) : poseAt(s, at));
    },
    [steps, board],
  );

  useEffect(() => {
    t.current = 0;
    setStepDone(false);
    show(step, 0);
    optsRef.current.onStep?.(step);
  }, [step, show]);

  useEffect(() => {
    if (!playing) return;
    const s = steps[step];
    if (!s) return;
    const rm = reducedMotion();
    const dur = rm ? Math.min(0.3, s.duration) : s.duration;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = ((now - last) / 1000) * speed;
      last = now;
      t.current = Math.min(dur, t.current + dt);
      show(step, rm ? (t.current >= dur ? s.duration : 0) : t.current);
      if (t.current >= dur) {
        setStepDone(true);
        const last = step >= steps.length - 1;
        if (last || optsRef.current.pauseAfter?.(step)) {
          setPlaying(false);
        } else {
          setStep(step + 1);
        }
        return;
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [playing, step, steps, speed, show]);

  const goTo = useCallback((i: number) => setStep(Math.max(0, Math.min(steps.length - 1, i))), [steps.length]);
  return {
    step,
    playing,
    speed,
    stepDone,
    finished: stepDone && step === steps.length - 1,
    play: () => {
      if (stepDone && step === steps.length - 1) {
        setStep(0);
      } else if (stepDone) {
        setStep(step + 1);
      }
      setPlaying(true);
    },
    pause: () => setPlaying(false),
    toggle: () => setPlaying((p) => !p),
    next: () => {
      if (step < steps.length - 1) {
        setStep(step + 1);
        setPlaying(true);
      }
    },
    prev: () => {
      setStep(Math.max(0, step - 1));
      setPlaying(false);
    },
    replay: () => {
      t.current = 0;
      setStepDone(false);
      show(step, 0);
      setPlaying(true);
    },
    restart: () => {
      setStep(0);
      setPlaying(true);
    },
    setSpeed,
    goTo,
  };
}
